import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import axios from "../../context/AxiosInstance";
import { toast } from "react-toastify";
import Select from "react-select";
import "../../styles/edit-salaire-modal.css";

const SalaireEditModal = ({ show, handleClose, salaire, token, contrats }) => {
  const parseNumberFr = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const s = String(val).replace(/\s/g, "").replace(/,/g, ".");
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  };

  const [formData, setFormData] = useState({
    montant_simple: "",
    taux_gain_salaire: 0,
    taux_retenu_salaire: 0,
    id_crg: null,
    mois_salaire: "",
  });

  const [crgs, setCrgs] = useState([]);
  const [loading, setLoading] = useState(false);

  const contrat = contrats.find((c) => c.id_contrat === salaire?.id_contrat);

  useEffect(() => {
    if (!salaire) return;

    const fetchCRGs = async () => {
      try {
        const res = await axios.get("/crg/", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const options = res.data.data.map((c) => ({
          value: c.id_crg,
          label: c.libelle_crg,
        }));

        setCrgs(options);

        const crgOption = options.find((c) => c.value === salaire.id_crg);

        setFormData({
          montant_simple: String(salaire.montant_simple ?? ""),
          taux_gain_salaire: salaire.taux_gain_salaire,
          taux_retenu_salaire: salaire.taux_retenu_salaire,
          id_crg: crgOption || null,
          mois_salaire: salaire.mois_salaire,
        });
      } catch (err) {
        toast.error("Erreur chargement des CRG");
      }
    };

    fetchCRGs();
  }, [salaire, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "montant_simple") {
      const next = value.replace(/\s/g, "");
      if (!/^[0-9,.]*$/.test(next)) return;
      setFormData((prev) => ({
        ...prev,
        montant_simple: next,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: parseNumberFr(value),
    }));
  };

  const handleUpdate = async () => {
    if (!formData.id_crg || !formData.mois_salaire) {
      toast.warning("Veuillez remplir tous les champs");
      return;
    }

    const montant_simple = parseNumberFr(formData.montant_simple);
    const { taux_gain_salaire, taux_retenu_salaire } = formData;
    const montant_gain_salaire = montant_simple * (taux_gain_salaire / 100);
    const montant_retenu_salaire = montant_simple * (taux_retenu_salaire / 100);

    const payload = {
      montant_simple,
      taux_gain_salaire,
      taux_retenu_salaire,
      montant_gain_salaire,
      montant_retenu_salaire,
    };

    setLoading(true);

    try {
      await axios.put(
        `/salaires/?id_contrat=${salaire.id_contrat}&id_crg=${salaire.id_crg}&mois_salaire=${salaire.mois_salaire}&annee_salaire=${salaire.annee_salaire}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Salaire modifié avec succès");
      handleClose();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data?.detail || "Erreur : Salaire déjà existant");
      } else {
        toast.error("Erreur lors de la modification");
      }
    } finally {
      setLoading(false);
    }
  };

  const montantSimpleNum = parseNumberFr(formData.montant_simple);
  const montant_gain_salaire = montantSimpleNum * (formData.taux_gain_salaire / 100);
  const montant_retenu_salaire = montantSimpleNum * (formData.taux_retenu_salaire / 100);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="edit-salaire-modal"
      backdrop="static"
    >
      <Modal.Header closeButton className="edit-salaire-modal-header">
        <div>
          <Modal.Title>Modifier le salaire</Modal.Title>
          <div className="edit-salaire-subtitle">
            Ajuster les montants et taux de cette ligne de paie
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="edit-salaire-modal-body">
        {contrat && (
          <div className="edit-salaire-context-card">
            <div className="label">Contexte</div>
            <div className="value">
              {contrat.prenom_employe} {contrat.nom_employe} — {contrat.poste}
            </div>
            <div className="edit-salaire-context-meta">
              {formData.mois_salaire && salaire?.annee_salaire && (
                <span className="meta-chip">
                  {formData.mois_salaire} {salaire.annee_salaire}
                </span>
              )}
              {formData.id_crg?.label && (
                <span className="meta-chip">{formData.id_crg.label}</span>
              )}
            </div>
          </div>
        )}

        <Form>
          <div className="edit-salaire-section">
            <div className="edit-salaire-section-title">Période et rubrique</div>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Mois</Form.Label>
                <Form.Control value={formData.mois_salaire} disabled readOnly />
              </Col>
              <Col md={6}>
                <Form.Label>CRG</Form.Label>
                <div className="edit-rs-select">
                  <Select
                    classNamePrefix="rs"
                    options={crgs}
                    value={formData.id_crg}
                    isDisabled
                    placeholder="CRG"
                    styles={{
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="edit-salaire-section">
            <div className="edit-salaire-section-title">Montants et taux</div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Montant simple</Form.Label>
                <Form.Control
                  type="text"
                  name="montant_simple"
                  value={formData.montant_simple}
                  onChange={handleChange}
                  inputMode="decimal"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Taux gain (%)</Form.Label>
                <Form.Control
                  type="number"
                  name="taux_gain_salaire"
                  value={formData.taux_gain_salaire}
                  onChange={handleChange}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Taux retenue (%)</Form.Label>
                <Form.Control
                  type="number"
                  name="taux_retenu_salaire"
                  value={formData.taux_retenu_salaire}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <p className="edit-salaire-hint">
              Les montants gain et retenue sont recalculés automatiquement à partir du montant simple et des taux.
            </p>
          </div>

          <div className="edit-salaire-section">
            <div className="edit-salaire-section-title">Montants calculés</div>
            <div className="edit-salaire-computed-grid">
              <div className="edit-salaire-computed-card gain">
                <div className="computed-label">Montant gain</div>
                <div className="computed-value">
                  {montant_gain_salaire.toLocaleString("fr-FR")}{" "}
                  <span className="computed-unit">GNF</span>
                </div>
              </div>
              <div className="edit-salaire-computed-card retenue">
                <div className="computed-label">Montant retenue</div>
                <div className="computed-value">
                  {montant_retenu_salaire.toLocaleString("fr-FR")}{" "}
                  <span className="computed-unit">GNF</span>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="edit-salaire-modal-footer">
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button className="btn-save" onClick={handleUpdate} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Enregistrement…
            </>
          ) : (
            "Enregistrer les modifications"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SalaireEditModal;
